import classNames from 'classnames';
import { AnchorLink } from 'components/anchorLink';
import { appInsight, collectProperties } from 'components/appInsight';
import { Icon, IconsEnum } from 'components/Icons';
import { AppFragment } from 'enums/path-fragment';
import { LiveScommessaPreMatch } from 'features/live/liveScommessaPreMatch';
import { LIVE_STREAMING_ID, VideoStreamingMode } from 'features/live/liveVideoStreaming';
import { LiveVideoStreamingButton } from 'features/live/liveVideoStreamingButton';
import { decodePathname } from 'features/sport';
import { useInfoTemplateAvvenimento } from 'features/sport/hooks';
import { selectAvvenimentoHeaderInfo, selectDettaglioAvvenimento } from 'features/sport/selectors';
import { setScoreboardHasError } from 'features/sport/sportSlice';
import { useGetLabelByKey, useTranslation } from 'hooks/useLingUI';
import useWindowSize from 'hooks/useWindowSize';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useRouter } from 'next/router';
import React, { memo, useEffect, useMemo } from 'react';
import { SportsCacheTemplateAvvenimentoDto } from 'types/swagger';
import { isMatch, toSportUrlCase } from 'utility/functions';
import { SportResultsAndInfoTrigger } from '../sportResultsAndInfoTrigger';
import { SportWidgetList } from '../sportWidgetList';
import { Widget } from '../sportWidgetList/SportWidgetList';
import styles from './SportAvvenimentoHeader.module.scss';
import { SportAvvenimentoHeaderTitle } from './sportAvvenimentoHeaderTitle';
import { ScoreBoard } from './sportScoreboard';

export type SportAvvenimentoHeaderProps = {
  widgetList?: Array<Widget>;
  noPaddingRight?: boolean;
};

export const SportAvvenimentoHeader = memo(function SportAvvenimentoheader({
  widgetList,
  noPaddingRight,
}: SportAvvenimentoHeaderProps) {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const dispatch = useAppDispatch();

  const linkBack = useMemo(() => {
    const [root, disciplina, next] = decodePathname(asPath).split('/');
    const list = ['', root, disciplina];
    if (isMatch(root, AppFragment.Sport)) {
      list.push(next);
    }

    const back = list.join('/');

    return toSportUrlCase(back);
  }, [asPath]);

  const { avvenimentoKey, providerId, betRadarMatchId, betGeniusMatchId, hasScoreBoard, coodsId } =
    useTypedSelector(selectAvvenimentoHeaderInfo);

  const { templateAvvenimento } = useInfoTemplateAvvenimento<SportsCacheTemplateAvvenimentoDto>();
  const { live } = templateAvvenimento ?? {};

  useEffect(() => {
    if (!hasScoreBoard && !providerId) {
      appInsight?.trackTrace({
        message: `Unable to visualize scoreboard`,
        properties: collectProperties(false, {
          betRadar: coodsId ?? betRadarMatchId,
          betGenius: betGeniusMatchId,
          avvenimentoKey,
        }),
      });
    }
  }, [avvenimentoKey, betGeniusMatchId, betRadarMatchId, coodsId, hasScoreBoard, providerId]);

  const { descrizioneManifestazione, descrizioneManifestazioneTrKey } =
    useTypedSelector(selectDettaglioAvvenimento) ?? {};

  const labelIndietro = useGetLabelByKey('indietro');

  const { isMobile } = useWindowSize();

  useEffect(() => {
    dispatch(setScoreboardHasError({ hasError: false }));
  }, [dispatch, asPath]);

  return (
    <React.Fragment>
      <div
        className={classNames(styles.wrapper, { [styles.sticky]: isMobile, [styles.noPaddingRight]: noPaddingRight })}
      >
        <AnchorLink href={linkBack} className={styles.backDsk}>
          <Icon iconId={IconsEnum.ARROW} className={styles.icon} color={'var(--color-primary'} />
          <span>{labelIndietro}</span>
        </AnchorLink>
        <div className={styles.gridContainer}>
          <AnchorLink href={linkBack} className={styles.back}>
            <Icon iconId={IconsEnum.ARROW} className={styles.icon} color={'var(--color-on-bg-primary'} />
          </AnchorLink>
          <span className={styles.manifestazione} suppressHydrationWarning={true}>
            {t(descrizioneManifestazioneTrKey, descrizioneManifestazione)}
          </span>
          <SportWidgetList key={`Widget-List-${avvenimentoKey}`} list={widgetList ?? []} className={styles.widget} />
          {live?.hasStreaming && <LiveVideoStreamingButton className={styles.streaming} />}
          {hasScoreBoard && (
            <SportResultsAndInfoTrigger key={`Results-Info-${avvenimentoKey}`} className={styles.stats} />
          )}
          <SportAvvenimentoHeaderTitle
            key={avvenimentoKey}
            className={styles.avvenimento}
            avvenimentoKey={avvenimentoKey}
          />
          <LiveScommessaPreMatch
            key={`Scommessa-${avvenimentoKey}`}
            className={classNames(styles.prematch, { [styles.hidden]: !isMobile })}
          />
        </div>
      </div>
      <div className={styles.driver} id={`${VideoStreamingMode.expanded}-${LIVE_STREAMING_ID}`} />
      {hasScoreBoard && <ScoreBoard key={`ScoreBoard-${avvenimentoKey}`} avvenimentoKey={avvenimentoKey} />}
      <hr data-hidden={isMobile} className={styles.space} />
    </React.Fragment>
  );
});
